// 261005 E3-P3a — logic THUẦN của xem thử / áp sửa tay ③ (không import antd — jest của react-scripts không load được antd v6).
import type { LocalizeKind, LocalizeOp, LocalizePatchResponse, LocalizePreviewResponse, LocalizeSkippedChapter, LocalizeViolation, RepushSkipReason } from '../../../types/adaptation';
import { friendlyApiMessage } from './jobErrorText';
import { redactMoney } from './moneyRedact';

export const SKIP_REASON_LABEL: Record<RepushSkipReason, string> = {
  keptForPlan: 'đã có Film Plan dựng từ chương này',
  keptEdited: 'đã sửa tay trong Chapters',
  chapterMissing: 'chương không còn trong Chapters',
  // 261023 E3-P3j2a (A1): không dùng qua `skipLine` kiểu «Tập n: …» (xem `skipLine`) — nhãn này chỉ để bản đồ đủ mã.
  QUALITY_NOT_READY: 'chưa đạt kiểm tra',
};

/** 261023 E3-P3j2a (A1): MỘT câu đúng mọi lý do chặn chất lượng (STALE / chưa kiểm xong / còn lỗi nặng) — không nút mới, không mã thô, không tiền. */
const qualityNotReadyLine = (episode: number): string => `Tập ${episode} chưa đạt kiểm tra nên chưa cập nhật sang Chapters.`;

export const skipLine = (s: LocalizeSkippedChapter): string => (s.reason === 'QUALITY_NOT_READY'
  ? qualityNotReadyLine(s.episode)
  : `Tập ${s.episode}: ${SKIP_REASON_LABEL[s.reason] ?? s.reason}`);

/** Ops từ ô "Tên trong phim": trống ⇒ `clearName` (nếu dòng đang có tên), khác ⇒ `renameEntity`. Không đổi ⇒ `null`. */
export const nameOps = (kind: LocalizeKind, id: string, current: string, next: string): LocalizeOp[] | null => {
  const n = next.trim(); const c = current.trim();
  if (n === c) return null;
  if (!n) return c ? [{ op: 'clearName', kind, id }] : null;
  return [{ op: 'renameEntity', kind, id, localizedName: n }];
};

/** Chỉ MỘT họ (một từ, không khoảng trắng). */
export const surnameInputError = (v: string): string | null => {
  const s = v.trim();
  if (!s) return 'Nhập họ mới';
  if (/\s/.test(s)) return 'Họ mới chỉ một từ';
  return null;
};

export interface PreviewSection { key: string; tone: 'error' | 'warning' | 'info' | 'success'; title: string; lines: string[] }

const pairLine = (p: { from: string; to: string }): string => `${p.from} → ${p.to}`;
const sceneList = (byScene: Record<string, number>): string => {
  const ks = Object.keys(byScene).filter((k) => k !== '-1').map(Number).sort((a, b) => a - b);
  return ks.length ? `cảnh ${ks.map((k) => k + 1).join(', ')}` : 'phần mở/kết tập';
};

/** Các khối hiển thị của modal xem thử, theo thứ tự quan trọng. Chỉ khối có nội dung. */
export const previewSections = (p: LocalizePreviewResponse): PreviewSection[] => {
  const out: PreviewSection[] = [];
  const add = (s: PreviewSection) => { if (s.lines.length) out.push(s); };
  add({ key: 'errors', tone: 'error', title: 'Lỗi chặn — không áp được', lines: p.violations.errors.map((v: LocalizeViolation) => v.message) });
  add({ key: 'rejected', tone: 'warning', title: 'Lệnh bị từ chối', lines: p.rejected.map((r) => r.reason) });
  if (p.world) {
    add({
      key: 'world', tone: 'error', title: 'Đổi bối cảnh phim — cảnh báo cứng',
      lines: [
        p.world.writtenEpisodes.length
          ? `${p.world.writtenEpisodes.length} tập đã viết (${p.world.writtenEpisodes.join(', ')}) sẽ hiện "Bối cảnh đã đổi — cần xem lại". Hệ thống KHÔNG tự sửa chữ.`
          : 'Chưa có tập nào đã viết.',
        p.world.filmPlanCount ? `${p.world.filmPlanCount} Film Plan đã dựng KHÔNG tự đổi theo bối cảnh mới.` : 'Chưa có Film Plan nào.',
        ...p.world.warnings.map((w) => w.message),
      ],
    });
  }
  if (p.renamePairs.length) add({ key: 'pairs', tone: 'info', title: 'Cặp đổi tên sẽ thay trên kịch bản đã viết', lines: p.renamePairs.map(pairLine) });
  const eps = p.impact.episodes.map((e) => `Tập ${e.episode}: ${e.replaced} chỗ (${sceneList(e.byScene)})`);
  const chain = p.touchedEpisodes.filter((t) => t.entryUpdated && t.textReplaced === 0).map((t) => `Tập ${t.episode}: chỉ nối lại đoạn kết tập trước (không đổi chữ)`);
  add({ key: 'impact', tone: 'info', title: `Kịch bản bị thay tên: ${p.impact.totalReplaced} chỗ`, lines: [...eps, ...chain] });
  add({ key: 'examples', tone: 'info', title: 'Ví dụ', lines: p.impact.byPair.flatMap((b) => b.examples.map((ex) => `${b.from} (×${b.count}): ${ex}`)) });
  add({
    key: 'chapters-update', tone: 'success', title: `Chapters sẽ được cập nhật (${p.chapters.willUpdate.length} tập)`,
    lines: p.chapters.willUpdate.length ? [p.chapters.willUpdate.map((c) => `tập ${c.episode}`).join(', ')] : [],
  });
  add({ key: 'chapters-skip', tone: 'warning', title: 'Chương Chapters sẽ KHÔNG được cập nhật — bạn tự xử', lines: p.chapters.skipped.map(skipLine) });
  add({ key: 'alias', tone: 'warning', title: 'Bí danh chưa tự đổi theo', lines: p.aliasUnmapped.length ? [`${p.aliasUnmapped.length} bí danh (${p.aliasUnmapped.slice(0, 5).map((a) => a.source).join(', ')}) — giữ nguyên, kiểm lại ở ②`] : [] });
  add({
    key: 'bare', tone: 'warning', title: 'Chữ gọi trống một họ chưa đổi',
    lines: (p.bareUnchanged ?? []).map((b) => `${b.count} chỗ gọi trống "${b.from}" chưa đổi sang "${b.to}" (còn thực thể khác có tên chứa "${b.from}") — sửa tay nếu cần`),
  });
  add({ key: 'warnings', tone: 'warning', title: 'Cảnh báo', lines: [...p.violations.warnings.map((v) => v.message), ...p.notes] });
  const info: string[] = [];
  if (p.info.unwrittenEpisodes > 0) info.push(`${p.info.unwrittenEpisodes} tập chưa viết sẽ dùng tên mới khi viết.`);
  if (p.info.seasonReaderWillBeStale) info.push('Kết quả "AI đọc cả mùa" sẽ thành cũ (cần đọc lại trước khi Đẩy thường nếu cổng bật).');
  if (p.info.assistantBlocksWillBeStale) info.push('Khối đề xuất sẵn sàng của Trợ lý ② / ④ sẽ thành cũ — chỉ phải xem thử lại, không mất dữ liệu.');
  add({ key: 'info', tone: 'info', title: 'Lưu ý', lines: info });
  return out;
};

/** Có gì để áp không (≥ 1 lệnh hợp lệ) và không có lỗi chặn. */
export const canApplyPreview = (p: LocalizePreviewResponse | null): boolean => !!p && !p.hasErrors && p.applied.length > 0;

export interface ApplyResultView { success: string; sections: PreviewSection[] }

/** Kết quả sau áp / khôi phục: giữ hiển thị tới khi người đóng. */
export const applyResultView = (r: LocalizePatchResponse): ApplyResultView => {
  const sections: PreviewSection[] = [];
  if (r.handoffError) sections.push({ key: 'handoff-error', tone: 'error', title: 'Đã ghi, nhưng đẩy lại sang Chapters bị lỗi', lines: [redactMoney(r.handoffError), 'Phần đã ghi giữ nguyên — vào "Đẩy sang Chapters" để thử lại.'] });
  if (r.handoff) {
    if (r.handoff.updated > 0) sections.push({ key: 'handoff-ok', tone: 'success', title: `Đã cập nhật ${r.handoff.updated} chương trong Chapters`, lines: [] });
    if (r.handoff.skipped.length) sections.push({ key: 'handoff-skip', tone: 'warning', title: 'Chương KHÔNG được cập nhật — bạn tự xử', lines: r.handoff.skipped.map(skipLine) });
  }
  if (r.rejected.length) sections.push({ key: 'rejected', tone: 'warning', title: 'Lệnh bị từ chối', lines: r.rejected.map((x) => x.reason) });
  const eps = r.writtenEpisodes.length ? `, ${r.writtenEpisodes.length} tập kịch bản đã đổi` : '';
  const success = r.written
    ? (r.restoredFrom ? `Đã khôi phục v${r.restoredFrom}${eps}` : `Đã áp ${r.applied.length} thao tác${eps}`)
    : 'Không có gì để ghi';
  return { success, sections };
};

const CODE_TEXT: Record<string, string> = {
  OUTLINE_CHANGED: 'Dữ liệu vừa bị đổi bởi thao tác khác — đã tải lại, xem thử lại rồi áp.',
  SCRIPT_CHANGED: 'Một kịch bản vừa bị sửa trong lúc áp — xem thử lại rồi áp.',
  JOB_RUNNING: 'Đang có tiến trình chạy cho truyện này — chờ xong rồi thử lại.',
  HAS_ERRORS: 'Còn lỗi chặn — sửa rồi xem thử lại.',
  // 261015 P5a (D6): 409 của op `revertAutoWorld` (Hoàn tác bối cảnh AI tự ghi) — mã BE `revertAutoWorldPrecheck`.
  NO_WORLD_AUTO: 'Không còn bối cảnh AI tự ghi để hoàn tác (đã được xử lý) — đã tải lại.',
  WORLD_CHANGED_SINCE_AUTO: 'Bối cảnh đã bị sửa sau khi AI ghi — không hoàn tác được nữa; dùng "Sửa" để chỉnh bối cảnh.',
  POLICY_CHANGED: 'Cách bản địa hoá đã đổi sau khi AI ghi bối cảnh — không hoàn tác được; dùng "Sửa" hoặc chạy lại bản địa hoá.',
  HAS_WRITTEN_EPISODES: 'Đã có tập kịch bản — bối cảnh không hoàn tác được nữa (đổi bối cảnh là quyết định của người); dùng "Sửa".',
};
const REFRESH_CODES = ['OUTLINE_CHANGED', 'SCRIPT_CHANGED', 'NO_WORLD_AUTO', 'WORLD_CHANGED_SINCE_AUTO', 'POLICY_CHANGED', 'HAS_WRITTEN_EPISODES'];

/** Câu hiển thị cho lỗi HTTP của xem thử / áp. */
export const editErrorText = (err: unknown): { text: string; refresh: boolean } => {
  const data = (err as any)?.response?.data;
  const code: string | undefined = typeof data?.code === 'string' ? data.code : undefined;
  const msg: string = typeof data?.message === 'string' ? friendlyApiMessage(data.message, '') : '';
  const text = (code && CODE_TEXT[code]) || msg || 'Không thực hiện được';
  return { text: code === 'HAS_ERRORS' && msg ? msg : text, refresh: !!code && REFRESH_CODES.includes(code) };
};

/** Mốc màn hình lệch mốc xem thử ⇒ dữ liệu đã đổi từ lúc người nhìn (so cửa sổ 2 ms, mốc thiếu ⇒ coi như khớp). */
export const screenStale = (screenModifiedAt: string | null | undefined, baseModifiedAt: string): boolean => {
  if (!screenModifiedAt) return false;
  const a = Date.parse(screenModifiedAt); const b = Date.parse(baseModifiedAt);
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) > 1;
};
